import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from './utils';
import { type ApiResponse } from './types';
import { type FuelCategories } from '@/generated/types/navigator/types.gen';
import { type MessageId } from '@/assets/lang/en-US';

export const wildfireApi = createApi({
  reducerPath: 'wildfireApi',
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    getRedFlagWarnings: builder.query<
      ApiResponse<RedFlagWarning, RedFlagWarningSummary>,
      null
    >({
      query: () => ({
        url: `/v1/wildfire/red_flag_warning`,
        method: 'GET',
      }),
    }),
  }),
});

export const { useGetRedFlagWarningsQuery } = wildfireApi;

export const resetWildfireApiState = wildfireApi.util.resetApiState;

export interface RedFlagWarning {
  id: string;
  geometry: GeoJSON.Feature;
  start_date: string;
  expiration_date: string;
  issued_date: string;
  description: string;
  area: string;
  severity: string;
  nws_id: string;
  coveredCircuits: number;
  coveredDistance: number;
}

interface RedFlagWarningSummary {
  count: number;
}

export interface FuelCategoryDisplay {
  category: keyof FuelCategories;
  value: number;
}

export const FUEL_CATEGORY_MESSAGE_IDS: Record<
  keyof FuelCategories,
  MessageId
> = {
  grass: 'fuelCategory.grass',
  shrub: 'fuelCategory.shrub',
  timber_litter: 'fuelCategory.timber_litter',
  slash_blowdown: 'fuelCategory.slash_blowdown',
  non_burnable: 'fuelCategory.non_burnable',
};

export const formatFuelCategoriesForDisplay = (
  fuelCategories: FuelCategories,
  minPercentageThreshold: number = 0
): FuelCategoryDisplay[] => {
  return Object.entries(fuelCategories)
    .filter(
      ([_, value]) =>
        value > 0 && Math.round(value * 100) >= minPercentageThreshold
    )
    .sort(([_, a], [__, b]) => b - a)
    .map(([category, value]) => ({
      category: category as keyof FuelCategories,
      value,
    }));
};
